"use client"

import { ChevronRight } from "lucide-react"
import { motion, useReducedMotion } from "motion/react"
import { cn } from "@strivelabs/ui-core"
import { DURATION_QUICK, EASE_OUT } from "@strivelabs/ui-core/v2"

export interface DisclosureProps {
  expanded: boolean
  busy?: boolean
  label: string
  controls?: string
  onToggle: () => void
  className?: string
}

/**
 * The chevron button that expands a nav node.
 *
 * Kept separate from the node's link so each keeps its own hit area and its own
 * accessible name — the link navigates, the button only expands.
 */
export function Disclosure({
  expanded,
  busy = false,
  label,
  controls,
  onToggle,
  className,
}: DisclosureProps) {
  const reduce = useReducedMotion()
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={expanded}
      aria-controls={controls}
      aria-busy={busy || undefined}
      aria-label={`${expanded ? "Collapse" : "Expand"} ${label}`}
      className={cn(
        // The visible glyph stays 15×20, but a pseudo-element grows the hit area
        // toward the 24px minimum — vertically and to the left, never right,
        // where it would overlap the adjacent nav link.
        "relative grid h-[26px] w-[14px] shrink-0 place-items-center rounded-v2-sm text-v2-sidebar-foreground/[0.62] before:absolute before:-inset-y-1 before:-left-2 before:right-0 before:content-['']",
        "transition-colors hover:text-v2-sidebar-foreground",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-v2-sidebar-ring",
        className,
      )}
    >
      <motion.span
        animate={{ rotate: expanded ? 90 : 0 }}
        initial={false}
        transition={reduce ? { duration: 0 } : { duration: DURATION_QUICK, ease: EASE_OUT }}
        className="grid place-items-center"
      >
        <ChevronRight size={12} strokeWidth={2.4} />
      </motion.span>
    </button>
  )
}
